import type { FitAddon } from "@xterm/addon-fit";
import type { Unicode11Addon } from "@xterm/addon-unicode11";
import type { WebglAddon } from "@xterm/addon-webgl";
import type { ITheme, Terminal } from "@xterm/xterm";
import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { env } from "../env/env";
import { store } from "../store";
import { $queuedTerminalPrompt } from "./queued-prompt.atom";
import {
	$spawnTerminal,
	$terminalState,
	TERMINAL_INSTANCE_KEY_SEPARATOR,
	type TerminalHandle,
	getDefaultShell,
	keyToString,
} from "./terminal.atom";
import type { TerminalInstanceKey } from "./terminal.types";

export const generateTerminalId = () => crypto.randomUUID();

// Lazy load xterm modules to avoid SSR issues (xterm uses browser-only globals)
let xtermModules: {
	Terminal: typeof Terminal;
	FitAddon: typeof FitAddon;
	Unicode11Addon: typeof Unicode11Addon;
	WebglAddon: typeof WebglAddon;
} | null = null;

async function getXtermModules() {
	if (xtermModules) {
		return xtermModules;
	}

	const [{ Terminal }, { FitAddon }, { WebglAddon }, { Unicode11Addon }] = await Promise.all([
		import("@xterm/xterm"),
		import("@xterm/addon-fit"),
		import("@xterm/addon-webgl"),
		import("@xterm/addon-unicode11"),
	]);

	xtermModules = { Terminal, FitAddon, WebglAddon, Unicode11Addon };
	return xtermModules;
}

export type TerminalTheme = "light" | "dark";

const logger = new Logger("terminal-instance.atom");

export const terminalThemes: Record<TerminalTheme, ITheme> = {
	light: {
		background: "#ffffff",
		foreground: "#000000",
		cursor: "#000000",
		cursorAccent: "#ffffff",
		selectionBackground: "#add6ff",
		selectionForeground: "#000000",
		black: "#000000",
		red: "#cd3131",
		green: "#00bc00",
		yellow: "#949800",
		blue: "#0451a5",
		magenta: "#bc05bc",
		cyan: "#0598bc",
		white: "#555555",
		brightBlack: "#666666",
		brightRed: "#cd3131",
		brightGreen: "#14ce14",
		brightYellow: "#b5ba00",
		brightBlue: "#0451a5",
		brightMagenta: "#bc05bc",
		brightCyan: "#0598bc",
		brightWhite: "#a5a5a5",
	},
	dark: {
		background: "#12131a",
		foreground: "#e4e8f8",
		cursor: "#a8b2e1",
		cursorAccent: "#12131a",
		selectionBackground: "#3d4063",
		selectionForeground: "#e4e8f8",
		black: "#12131a",
		red: "#f87171",
		green: "#4ade80",
		yellow: "#facc15",
		blue: "#60a5fa",
		magenta: "#c084fc",
		cyan: "#22d3ee",
		white: "#e4e8f8",
		brightBlack: "#6b7280",
		brightRed: "#fca5a5",
		brightGreen: "#86efac",
		brightYellow: "#fde047",
		brightBlue: "#93c5fd",
		brightMagenta: "#d8b4fe",
		brightCyan: "#67e8f9",
		brightWhite: "#ffffff",
	},
};

type Disposable = { dispose: () => void };

export type TerminalInstance = {
	terminal: Terminal;
	fitAddon: FitAddon;
	webglAddon: WebglAddon | null;
	unicode11Addon: Unicode11Addon;
	container: HTMLDivElement;
	ptyHandle: TerminalHandle | null;
	/** Disposables for PTY-related event handlers that must be cleaned up on restart */
	ptyDisposables: Disposable[];
	isReady: boolean;
	height: number;
	title: string | null;
	cwd?: string;
	theme: TerminalTheme;
	/** Optional command to run instead of default shell */
	command?: string;
	/** Optional args for the command */
	commandArgs?: string[];
};

export const $terminalInstances = atom(new Map<string, TerminalInstance>());
$terminalInstances.debugLabel = "terminalInstances";

export const $orderedTerminalInstanceKeys = atom<string[]>([]);
$orderedTerminalInstanceKeys.debugLabel = "orderedTerminalInstanceKeys";

export const $activeTerminalInstanceKey = atom(undefined as string | undefined);
$activeTerminalInstanceKey.debugLabel = "activeTerminalInstanceKey";

export const $terminalInstance = atomFamily(
	(key: TerminalInstanceKey) => atom((get) => get($terminalInstances).get(keyToString(key))),
	isEqual
);

export type InitializeTerminalParams = {
	key: TerminalInstanceKey;
	cwd?: string;
	theme?: TerminalTheme;
	/** Optional command to run instead of default shell */
	command?: string;
	/** Optional args for the command */
	commandArgs?: string[];
};

// Phase 1: Create terminal UI only (no PTY yet)
export const $initializeTerminal = atom(
	null,
	async (get, set, params: InitializeTerminalParams) => {
		const { key, cwd, theme = "dark", command, commandArgs } = params;
		const keyStr = keyToString(key);
		const instances = get($terminalInstances);

		if (instances.has(keyStr)) {
			return instances.get(keyStr);
		}

		const { Terminal, FitAddon, WebglAddon, Unicode11Addon } = await getXtermModules();

		const container = document.createElement("div");
		container.style.width = "100%";
		container.style.height = "100%";

		const terminal = new Terminal({
			cursorBlink: true,
			fontSize: 12,
			fontWeight: "300",
			fontFamily: '"JetBrains Mono", "IBM Plex Mono", Menlo, Consolas, Monaco, monospace',
			theme: terminalThemes[theme],
			scrollback: 10000,
			// This is required by the Unicode11Addon.
			allowProposedApi: true,
		});

		const fitAddon = new FitAddon();
		terminal.loadAddon(fitAddon);
		terminal.open(container);

		// Wait for fonts to load before any fit() calls to ensure correct column calculation
		try {
			await document.fonts.ready;
			await document.fonts.load('12px "JetBrains Mono"');
		} catch {
			// Font loading is best-effort
		}

		let webglAddon: InstanceType<typeof WebglAddon> | null = null;
		try {
			webglAddon = new WebglAddon();
			terminal.loadAddon(webglAddon);
		} catch {
			logger.warn(
				"[terminal-instance.atom] WebGL not available, falling back to canvas renderer"
			);
		}

		const unicode11Addon = new Unicode11Addon();
		terminal.loadAddon(unicode11Addon);
		terminal.unicode.activeVersion = "11";

		const instance: TerminalInstance = {
			terminal,
			fitAddon,
			webglAddon,
			unicode11Addon,
			container,
			ptyHandle: null,
			ptyDisposables: [],
			isReady: false,
			height: 309,
			title: null,
			cwd,
			theme,
			command,
			commandArgs,
		};

		// Send distinct escape sequence (CSI u encoding) for Shift+Enter so
		// terminal applications (e.g. Claude Code) can distinguish it from
		// plain Enter and support multi-line input.
		// We must preventDefault on keydown to suppress the subsequent keypress
		// event, which would otherwise cause xterm.js to also emit \r via onData.
		terminal.attachCustomKeyEventHandler((event: KeyboardEvent) => {
			if (
				event.key === "Enter" &&
				event.shiftKey &&
				!event.ctrlKey &&
				!event.altKey &&
				!event.metaKey
			) {
				if (event.type === "keydown") {
					event.preventDefault();
					instance.ptyHandle?.write("\x1b[13;2u");
				}
				return false;
			}
			return true;
		});

		terminal.onTitleChange((title) => {
			instance.title = title;
			const current = store.get($terminalInstances);
			const newMap = new Map(current);
			newMap.set(keyStr, { ...instance });
			store.set($terminalInstances, newMap);
		});

		const next = new Map(instances);
		next.set(keyStr, instance);
		set($terminalInstances, next);

		set($orderedTerminalInstanceKeys, [...get($orderedTerminalInstanceKeys), keyStr]);

		return instance;
	}
);

// Phase 2: Connect PTY after terminal is attached and sized correctly
export const $connectTerminalPty = atom(
	null,
	async (get, set, key: TerminalInstanceKey): Promise<TerminalHandle | null> => {
		const keyStr = keyToString(key);
		const instances = get($terminalInstances);
		const instance = instances.get(keyStr);

		if (!instance) {
			logger.error("[terminal-instance.atom] Cannot connect PTY: instance not found", {
				key: keyStr,
			});
			return null;
		}

		// Check if we have a handle AND the PTY is still running
		// The state in packages/state is the source of truth for PTY lifecycle
		const terminalState = get($terminalState(key));
		if (instance.ptyHandle && terminalState.status === "running") {
			return instance.ptyHandle;
		}

		// Clear stale handle and disposables if PTY exited
		if (instance.ptyHandle && terminalState.status !== "running") {
			instance.ptyHandle = null;
			for (const d of instance.ptyDisposables) {
				d.dispose();
			}
			instance.ptyDisposables = [];
		}

		const { terminal, cwd, command, commandArgs } = instance;

		const cols = terminal.cols;
		const rows = terminal.rows;

		const shell = await getDefaultShell();

		logger.info("[connectTerminalPty] Spawning with", {
			command: command || shell.path,
			commandArgs: commandArgs || [],
			instanceCommand: command,
			instanceCommandArgs: commandArgs,
		});

		const isDefaultShell = !command;
		const defaultShellArgs = !env.isWindows ? ["-l"] : [];

		const ptyHandle = await store.set($spawnTerminal, {
			key,
			options: {
				command: command || shell.path,
				args: isDefaultShell ? defaultShellArgs : commandArgs || [],
				cwd,
				cols,
				rows,
			},
		});

		if (!ptyHandle) {
			logger.error("[terminal-instance.atom] Failed to spawn PTY");
			if (command) {
				logger.info(
					"[terminal-instance.atom] Custom command failed, falling back to default shell"
				);
				instance.command = undefined;
				instance.commandArgs = undefined;

				const current = store.get($terminalInstances);
				const newMap = new Map(current);
				newMap.set(keyStr, { ...instance });
				store.set($terminalInstances, newMap);

				return store.set($connectTerminalPty, key);
			}
			return null;
		}

		instance.ptyHandle = ptyHandle;

		// Buffer recent output to detect when an agent CLI is ready for input
		let promptBuffer = "";
		let promptFlushed = false;
		const decoder = new TextDecoder();
		const ansiRegex =
			// eslint-disable-next-line no-control-regex
			/\x1b\[[\d;?>=]*[A-Za-z$p]|\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)|\x1b[P_][^\x1b]*\x1b\\|\x1b[()][AB012]|\x1b\[>[\d;]*[a-z]/g;
		const bannerRegex = /GitHub\s?Copilot|Claude\s?Code|claude-code|Welcome\s?to/i;
		const codexReadyRegex = /OpenAI\s?Codex|model:\s*codex|codex\s+CLI/i;
		// Copilot's TUI mangles banner text via cursor positioning, so we
		// detect readiness by matching interactive-prompt-only text instead.
		const copilotReadyRegex = /Type\s?@|switch\s?mode/i;
		const copilotTrustRegex = /to\s?navigate|to\s?select|untrusted/i;
		const claudeTrustRegex =
			/No,\s?exit|review\s?what'?s\s?in\s?this\s?folder|Enter\s?to\s?confirm/i;
		let copilotReadyTimer: ReturnType<typeof setTimeout> | null = null;
		let claudeReadyTimer: ReturnType<typeof setTimeout> | null = null;
		let codexReadyTimer: ReturnType<typeof setTimeout> | null = null;

		let flushTimer: ReturnType<typeof setTimeout> | null = null;
		let flushDelayTimer: ReturnType<typeof setTimeout> | null = null;

		const flushQueuedPrompt = () => {
			if (promptFlushed || !instance.ptyHandle) {
				return;
			}
			const queued = store.get($queuedTerminalPrompt(key.documentId));
			if (!queued) {
				return;
			}
			promptFlushed = true;
			store.set($queuedTerminalPrompt(key.documentId), null);
			const text = queued.replace(/\r$/, "");
			const delay = command === "claude" ? 3000 : command === "codex" ? 1500 : 500;
			flushTimer = setTimeout(() => {
				if (!instance.ptyHandle) {
					return;
				}
				ptyHandle.write(`\x1b[200~${text}\x1b[201~`);
				flushDelayTimer = setTimeout(() => {
					if (!instance.ptyHandle) {
						return;
					}
					ptyHandle.write("\r");
				}, delay);
			}, 500);
		};

		const ptyDataDisposable = ptyHandle.onData((data: Uint8Array) => {
			terminal.write(data);
			if (!instance.isReady) {
				instance.isReady = true;
				const current = store.get($terminalInstances);
				const newMap = new Map(current);
				newMap.set(keyStr, { ...instance });
				store.set($terminalInstances, newMap);
			}

			// Watch output for agent prompt to flush queued command
			if (!promptFlushed && command) {
				const queued = store.get($queuedTerminalPrompt(key.documentId));
				if (queued) {
					const chunk = decoder.decode(new Uint8Array(data));
					promptBuffer = (promptBuffer + chunk).slice(-1024);
					const clean = promptBuffer.replace(ansiRegex, "");

					if (command === "copilot") {
						// Copilot's TUI renders the interactive prompt UI text
						// ("Type @", "switch mode") even on the splash/trust
						// screens. We debounce: schedule a flush when ready text
						// appears, but cancel it if trust text arrives later.
						if (copilotTrustRegex.test(clean)) {
							if (copilotReadyTimer) {
								clearTimeout(copilotReadyTimer);
								copilotReadyTimer = null;
							}
							promptBuffer = "";
						} else if (copilotReadyRegex.test(clean) && !copilotReadyTimer) {
							copilotReadyTimer = setTimeout(() => {
								// Re-check buffer — if trust text snuck in, skip
								const latest = promptBuffer.replace(ansiRegex, "");
								if (!copilotTrustRegex.test(latest)) {
									flushQueuedPrompt();
								} else {
									promptBuffer = "";
								}
								copilotReadyTimer = null;
							}, 2000);
						}
					} else if (command === "codex") {
						if (codexReadyRegex.test(clean) && !codexReadyTimer) {
							codexReadyTimer = setTimeout(() => {
								flushQueuedPrompt();
								codexReadyTimer = null;
							}, 1000);
						}
					} else if (command === "claude") {
						if (claudeTrustRegex.test(clean)) {
							if (claudeReadyTimer) {
								clearTimeout(claudeReadyTimer);
								claudeReadyTimer = null;
							}
							promptBuffer = "";
						} else if (bannerRegex.test(clean) && !claudeReadyTimer) {
							claudeReadyTimer = setTimeout(() => {
								const latest = promptBuffer.replace(ansiRegex, "");
								if (!claudeTrustRegex.test(latest)) {
									flushQueuedPrompt();
								} else {
									promptBuffer = "";
								}
								claudeReadyTimer = null;
							}, 1000);
						}
					} else if (bannerRegex.test(clean)) {
						flushQueuedPrompt();
					}
				} else {
					promptFlushed = true;
				}
			}
		});

		const terminalDataDisposable = terminal.onData((data: string) => {
			ptyHandle.write(data);
		});

		const terminalResizeDisposable = terminal.onResize(
			(size: { cols: number; rows: number }) => {
				ptyHandle.resize(size.cols, size.rows);
			}
		);

		const ptyExitDisposable = ptyHandle.onExit(async () => {
			if (copilotReadyTimer) {
				clearTimeout(copilotReadyTimer);
				copilotReadyTimer = null;
			}
			if (claudeReadyTimer) {
				clearTimeout(claudeReadyTimer);
				claudeReadyTimer = null;
			}
			if (codexReadyTimer) {
				clearTimeout(codexReadyTimer);
				codexReadyTimer = null;
			}
			if (flushTimer) {
				clearTimeout(flushTimer);
				flushTimer = null;
			}
			if (flushDelayTimer) {
				clearTimeout(flushDelayTimer);
				flushDelayTimer = null;
			}

			for (const d of instance.ptyDisposables) {
				d.dispose();
			}
			instance.ptyDisposables = [];
			instance.ptyHandle = null;

			// If the exited command was a custom command (not default shell),
			// automatically drop into the default shell
			if (command) {
				logger.info("[connectTerminalPty] Command exited, dropping to default shell", {
					exitedCommand: command,
				});

				// Clear the custom command so we spawn the default shell
				instance.command = undefined;
				instance.commandArgs = undefined;

				const current = store.get($terminalInstances);
				const newMap = new Map(current);
				newMap.set(keyStr, { ...instance });
				store.set($terminalInstances, newMap);

				// Reconnect with default shell
				await store.set($connectTerminalPty, key);
			} else {
				// Default shell exited - just update state
				const current = store.get($terminalInstances);
				const newMap = new Map(current);
				newMap.set(keyStr, { ...instance });
				store.set($terminalInstances, newMap);
			}
		});

		// Store disposables so they can be cleaned up on restart
		instance.ptyDisposables = [
			ptyDataDisposable,
			terminalDataDisposable,
			terminalResizeDisposable,
			ptyExitDisposable,
		];

		// Sync size in case it changed during async spawn
		const currentCols = terminal.cols;
		const currentRows = terminal.rows;
		if (currentCols !== cols || currentRows !== rows) {
			ptyHandle.resize(currentCols, currentRows);
		}

		const newMap = new Map(instances);
		newMap.set(keyStr, { ...instance });
		set($terminalInstances, newMap);

		return ptyHandle;
	}
);

export const $setTerminalHeight = atom(
	null,
	(get, set, params: { key: TerminalInstanceKey; height: number }) => {
		const { key, height } = params;
		const keyStr = keyToString(key);
		const instances = get($terminalInstances);
		const instance = instances.get(keyStr);

		if (instance) {
			instance.height = height;
			const newMap = new Map(instances);
			newMap.set(keyStr, { ...instance });
			set($terminalInstances, newMap);
		}
	}
);

/**
 * Destroys a single terminal instance, disposing all resources.
 */
export const $destroyTerminalInstance = atom(null, (get, set, key: TerminalInstanceKey) => {
	const keyStr = keyToString(key);
	const instances = get($terminalInstances);
	const instance = instances.get(keyStr);

	if (instance) {
		// Dispose PTY-related handlers first
		for (const d of instance.ptyDisposables) {
			d.dispose();
		}
		instance.ptyHandle?.kill();
		instance.webglAddon?.dispose();
		instance.terminal.dispose();
		instance.container.remove();

		const next = new Map(instances);
		next.delete(keyStr);
		set($terminalInstances, next);

		set(
			$orderedTerminalInstanceKeys,
			get($orderedTerminalInstanceKeys).filter((v) => v !== keyStr)
		);
	}
});

/**
 * Destroys all terminal instances for a given document.
 * Only called when the user explicitly kills all terminals for a document.
 * NOT called during document purge/refresh — terminal lifecycle is independent.
 */
export const $destroyTerminalInstancesForDocument = atom(null, (get, set, documentId: string) => {
	const instances = get($terminalInstances);

	for (const [keyStr, instance] of instances) {
		if (keyStr.startsWith(`${documentId}:`)) {
			for (const d of instance.ptyDisposables) {
				d.dispose();
			}
			instance.ptyHandle?.kill();
			instance.webglAddon?.dispose();
			instance.terminal.dispose();
			instance.container.remove();
		}
	}

	const next = new Map(instances);
	const prefix = `${documentId}${TERMINAL_INSTANCE_KEY_SEPARATOR}`;
	for (const keyStr of instances.keys()) {
		if (keyStr.startsWith(prefix)) {
			next.delete(keyStr);
		}
	}
	set($terminalInstances, next);
	set(
		$orderedTerminalInstanceKeys,
		get($orderedTerminalInstanceKeys).filter((v) => !v.startsWith(prefix))
	);
});

export const $updateTerminalTheme = atom(
	null,
	(get, set, params: { key: TerminalInstanceKey; theme: TerminalTheme }) => {
		const { key, theme } = params;
		const keyStr = keyToString(key);
		const instances = get($terminalInstances);
		const instance = instances.get(keyStr);

		if (instance && instance.theme !== theme) {
			instance.terminal.options.theme = terminalThemes[theme];
			instance.theme = theme;
			const newMap = new Map(instances);
			newMap.set(keyStr, { ...instance });
			set($terminalInstances, newMap);
		}
	}
);

export type RestartTerminalParams = {
	key: TerminalInstanceKey;
	/** Required when reinitializing from scratch (no existing instance) */
	cwd?: string;
	theme?: TerminalTheme;
};

export const $restartTerminal = atom(null, async (get, set, params: RestartTerminalParams) => {
	const { key, cwd, theme = "dark" } = params;
	const keyStr = keyToString(key);
	const instances = get($terminalInstances);
	let instance = instances.get(keyStr);

	if (!instance) {
		// No instance exists - need to initialize from scratch
		if (!cwd) {
			logger.error(
				"[terminal-instance.atom] Cannot restart: no instance and no cwd provided"
			);
			return;
		}
		instance = await set($initializeTerminal, { key, cwd, theme });
		if (!instance) {
			return;
		}
	} else {
		// Dispose PTY-related handlers first to prevent them from referencing the dead PTY
		for (const d of instance.ptyDisposables) {
			d.dispose();
		}
		instance.ptyDisposables = [];

		// Kill existing PTY
		instance.ptyHandle?.kill();
		instance.ptyHandle = null;
		instance.isReady = false;

		// Clear terminal screen
		instance.terminal.clear();

		// Update instance state
		const newMap = new Map(get($terminalInstances));
		newMap.set(keyStr, { ...instance });
		set($terminalInstances, newMap);
	}

	await set($connectTerminalPty, key);
});
$restartTerminal.debugLabel = "restartTerminal";
